import { useState } from 'react';
import { Button, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Play } from 'react-feather';
import {
  matchingSessionsApi,
  type MatchingReadiness,
} from '../../../../services/api/matchingSessions';

interface StartMatchingButtonProps {
  cohortId: string;
  groupsPrereqsMet: boolean;
  /**
   * Pre-flight readiness from the backend. While loading this will be
   * `undefined` — the button stays disabled until we have an answer to avoid
   * letting the admin click before we know whether they should be able to.
   */
  readiness: MatchingReadiness | undefined;
}

/**
 * User-actionable error messages keyed by the medley-api `code` field
 * (set by `MatchingSessionController.createForCohort`'s error mapping).
 * Falls back to the apiClient-enhanced `message` if the code isn't mapped,
 * then to a generic retry message if neither is present.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  COHORT_NO_INTAKE_FORM:
    'This cohort needs an intake form configured. Set one in cohort settings before starting matching.',
  NO_ELIGIBLE_PARTICIPANTS:
    'No participants have time slot availability set. Participants need to complete their intake before matching can start.',
  MATCHING_SERVER_AUTH_REJECTED:
    'Authentication with the matching service failed. Contact support.',
  MATCHING_SERVER_UNREACHABLE:
    "Couldn't reach the matching service. Try again in a moment, or contact support if it persists.",
  MATCHING_SERVER_INVALID_RESPONSE:
    'The matching service returned an unexpected response. Contact support.',
  MATCHING_SERVER_NOT_CONFIGURED:
    'The matching service is not configured for this environment. Contact support.',
};

function deriveErrorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string };
  if (e?.code && ERROR_CODE_MESSAGES[e.code]) return ERROR_CODE_MESSAGES[e.code];
  if (e?.message) return e.message;
  return 'Try again or contact support if the problem persists.';
}

/**
 * "Start Matching" primary button for the cohort Groups tab (MED-829 / E23-8 AC1-AC4).
 *
 * - Click → POST /api/cohorts/:cohortId/matching-sessions and navigate the
 *   matching_url in a new tab so the SGOS tab stays open as the admin's
 *   anchor back to the cohort. Falls back to in-tab nav if a popup blocker
 *   refuses the new-tab open. (Spec §5 said "no new tab opening required",
 *   meaning permitted but not mandated — we picked permitted to address the
 *   "no UI to come back to SGOS" UX gap surfaced post-implementation.)
 * - In-flight: button shows loading state and is disabled (AC2).
 * - On error: toast with retry messaging; do NOT navigate (AC3); orphan tab
 *   is closed.
 * - Disabled when groupsPrereqsMet is false (AC4 — same blocking conditions
 *   as the legacy Upload Matched Groups button). The "Edit Cohort Details"
 *   CTA is rendered by the parent's existing prereqs Alert.
 */
export function StartMatchingButton({
  cohortId,
  groupsPrereqsMet,
  readiness,
}: StartMatchingButtonProps) {
  const [isStarting, setIsStarting] = useState(false);

  // Disable when (a) cohort-level prereqs (session dates / time slots) aren't
  // met, (b) readiness is still loading, or (c) backend reports blockers.
  const isReadinessLoading = readiness === undefined;
  const hasReadinessBlockers = readiness !== undefined && !readiness.ready;
  const isDisabled = !groupsPrereqsMet || isReadinessLoading || hasReadinessBlockers;

  // Tooltip content — explain why the button is disabled. Skip the tooltip
  // entirely when the button is enabled so it doesn't pop on hover.
  const tooltipReasons: string[] = [];
  if (!groupsPrereqsMet) {
    tooltipReasons.push('Cohort needs session dates and time slots configured (see the alert above the panel).');
  }
  if (hasReadinessBlockers && readiness) {
    for (const blocker of readiness.blocking_reasons) {
      tooltipReasons.push(blocker.message);
    }
  } else if (isReadinessLoading) {
    tooltipReasons.push('Checking matching readiness…');
  }

  const handleClick = async () => {
    // Open a placeholder tab synchronously so the browser counts it as a
    // user-initiated open and doesn't block the popup. Severing `opener`
    // immediately blocks reverse-tabnabbing before we navigate cross-origin.
    const newTab = window.open('about:blank', '_blank');
    if (newTab) newTab.opener = null;

    setIsStarting(true);
    try {
      const response = await matchingSessionsApi.start(cohortId);
      if (newTab && !newTab.closed) {
        newTab.location.href = response.matching_url;
      } else {
        // Popup blocked OR user closed the placeholder tab before we resolved.
        // Fall back to in-tab navigation per spec §5.
        window.location.href = response.matching_url;
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      notifications.show({
        color: 'red',
        title: "Couldn't start matching",
        message: deriveErrorMessage(err),
      });
      setIsStarting(false);
    }
  };

  const button = (
    <Button
      leftSection={<Play size={18} />}
      onClick={handleClick}
      loading={isStarting}
      disabled={isDisabled}
    >
      Start Matching
    </Button>
  );

  // When the button is disabled, wrap in a Tooltip explaining why. The
  // wrapping <span> is required so Mantine's Tooltip can attach hover/focus
  // listeners — `disabled` buttons don't fire events themselves, but the span
  // does. `events.touch=true` makes it accessible on touch devices too.
  if (isDisabled && tooltipReasons.length > 0) {
    const label =
      tooltipReasons.length === 1
        ? tooltipReasons[0]
        : tooltipReasons.map((r) => `• ${r}`).join('\n');
    return (
      <Tooltip
        label={label}
        multiline
        w={320}
        withArrow
        events={{ hover: true, focus: true, touch: true }}
      >
        <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>{button}</span>
      </Tooltip>
    );
  }

  return button;
}
